import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"

import { removeReaction, selectReactions } from "src/store/game"
import { selectEconomyConfig } from "src/store/common"
import { playUiSound } from "src/utils/audio"
import { botLineEmoji, botLineText } from "src/utils/botLines"
import {
  REACTION_LIFETIME_MS,
  reactionById,
  reactionEmoji,
  reactionImage,
  reactionShowsText,
  reactionText,
} from "src/utils/reactions"

import "./ReactionOverlay.css"

/* What everybody sees when somebody presses a reaction: the emoji and one line, in the middle of
   the map, drifting up and fading.

   The middle rather than over the sender's cells, which was the other obvious place. Two reasons
   it is not: a player whose cells are off-screen — or under fog — would be reacting into nowhere,
   and the point of the feature is that the *other* side sees it. The name says who it was.

   Nothing here is stored. Each one removes itself after `REACTION_LIFETIME_MS`, and the reducer
   caps how many can be up at once, so a flood cannot bury the map.

   The bots talk here too since 2026-09-09 (owner: their lines read wrong in the chat, which is
   where the players talk to each other). A bot's bubble carries a `botCode` instead of a
   catalogue id — nothing it owns, nothing anybody bought — and is worded by `utils/botLines`
   from its *side*, so the horde growls and humanity does not. Everything else about it is a
   reaction: same bubble, same four seconds, same screen budget. */

const Reaction = ({ reaction, catalogue }) => {
  const dispatch = useDispatch()
  const bot = Boolean(reaction.botCode)
  const entry = bot ? null : reactionById(catalogue, reaction.reaction)

  // A bot's line is heard when it is shown, after the round's show (requirement 36, M9); a
  // person's gesture rang as it arrived (`startWebSocket`).
  useEffect(() => {
    if (bot) playUiSound("reaction")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timer = setTimeout(
      () => dispatch(removeReaction({ eventId: reaction.eventId })),
      REACTION_LIFETIME_MS
    )
    return () => clearTimeout(timer)
  }, [dispatch, reaction.eventId])

  const picture = bot ? null : reactionImage(entry)
  const emoji = bot ? botLineEmoji(reaction.botCode) : reactionEmoji(entry)
  const line = bot ? botLineText(reaction.botCode, reaction.team) : reactionText(entry)
  const showsLine = bot ? Boolean(line) : reactionShowsText(entry)

  return (
    <div className={`reaction-bubble reaction-${reaction.team}${picture ? " reaction-picture" : ""}`}>
      {picture ? (
        <img className="reaction-image" src={picture} alt="" />
      ) : (
        <span className="reaction-emoji" aria-hidden="true">
          {emoji}
        </span>
      )}
      <span className="reaction-body">
        <span className="reaction-who">{reaction.username}</span>
        {/* An id this build has never heard of still shows the name and the dot: knowing that
            somebody said something beats a silent gap. A picture reaction shows the name and no
            line — the image is the whole message. */}
        {showsLine && <span className="reaction-line">{line}</span>}
      </span>
    </div>
  )
}

/* `holdBotLines` while the round's show plays (requirement 36, M9): a bot speaks about the round
   that just resolved, so its bubble waits until the round has been seen, and its four seconds start
   then. A person's gesture is not about the round and shows at once. */
const ReactionOverlay = ({ holdBotLines = false }) => {
  const reactions = useSelector(selectReactions)
  const economy = useSelector(selectEconomyConfig)
  const shown = holdBotLines ? reactions.filter((reaction) => !reaction.botCode) : reactions

  if (!shown.length) return null

  return (
    <div className="reaction-overlay" aria-live="polite">
      {shown.map((reaction) => (
        <Reaction key={reaction.eventId} reaction={reaction} catalogue={economy?.cosmetics} />
      ))}
    </div>
  )
}

export default ReactionOverlay
